import { useEffect, useState } from 'react';
import axios from 'axios';
import getCurrentUserId from '@/utils/currentUser';

/**
 * OWN NAME — DP63 Part 2.
 *
 * ONE source for the logged-in user's name: GET /api/users/{id}/profile,
 * first_name + last_name. The same source DP61 Part 5 established for the avatar.
 * Not a caller's hint, not localStorage, and never a literal.
 *
 * When it is unavailable the fallbacks are NEUTRAL and contain no name:
 *   firstName -> 'there'   (so "Hi there", never "Hi Chad")
 *   fullName  -> ''        (so a label renders blank rather than someone else)
 *   initials  -> ''
 *
 * Cached at module level: one request per page load however many components ask.
 */
let cached = null;
let inflight = null;

const load = () => {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  const id = getCurrentUserId();
  if (!id) return Promise.resolve(null);
  inflight = axios
    .get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${id}/profile`)
    .then((r) => {
      const d = r.data || {};
      const full = [d.first_name, d.last_name].filter(Boolean).join(' ').trim();
      cached = {
        firstName: d.first_name || '',
        fullName: full,
        initials: [d.first_name, d.last_name]
          .filter(Boolean)
          .map((n) => n[0])
          .join('')
          .toUpperCase(),
        email: d.email || '',
      };
      return cached;
    })
    .catch(() => null)
    .finally(() => { inflight = null; });
  return inflight;
};

export function useOwnName() {
  const [me, setMe] = useState(cached);
  useEffect(() => {
    if (cached) return;
    let alive = true;
    load().then((p) => { if (alive && p) setMe(p); });
    return () => { alive = false; };
  }, []);
  return {
    firstName: me?.firstName || 'there',
    fullName: me?.fullName || '',
    initials: me?.initials || '',
    resolved: Boolean(me),
  };
}

export default useOwnName;
